import React, { useMemo, useState } from 'react';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { HeroSection } from '../components/marketplace/HeroSection';
import { CategoryChips } from '../components/marketplace/CategoryChips';
import { RestaurantCard } from '../components/marketplace/RestaurantCard';
import { useMarketplaceData } from '../lib/hooks';
import { EmptyState } from '../components/ui/EmptyState';
import { Store } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { isRestaurantOpenBySchedule } from '../lib/schedule';
export function HomePage() {
  const {
    categories,
    featuredRestaurants,
    allRestaurants,
    restaurantCategoryMap,
    zones,
    loading
  } = useMarketplaceData();
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const {
    t
  } = useLanguage();
  // Filter restaurants by category and search
  const filterRestaurants = useMemo(() => {
    return (restaurants: typeof allRestaurants) => {
      let filtered = restaurants;
      // Filter by category
      if (activeCategory) {
        filtered = filtered.filter(r => {
          const cats = restaurantCategoryMap[r.id];
          return cats && cats.includes(activeCategory);
        });
      }
      // Filter by search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        filtered = filtered.filter(r => r.name.toLowerCase().includes(q) || r.description_short && r.description_short.toLowerCase().includes(q));
      }
      return filtered;
    };
  }, [activeCategory, searchQuery, restaurantCategoryMap]);
  // Sort helper: open restaurants first, closed after
  const sortByOpenStatus = (restaurants: typeof allRestaurants) => {
    return [...restaurants].sort((a, b) => {
      const aStatus = isRestaurantOpenBySchedule(a.slug);
      const bStatus = isRestaurantOpenBySchedule(b.slug);
      const aOpen = aStatus.hasSchedule ? aStatus.isOpen : false;
      const bOpen = bStatus.hasSchedule ? bStatus.isOpen : false;
      if (aOpen === bOpen) return 0;
      return aOpen ? -1 : 1;
    });
  };
  const filteredFeatured = useMemo(() => sortByOpenStatus(filterRestaurants(featuredRestaurants)), [filterRestaurants, featuredRestaurants]);
  const filteredAll = useMemo(() => sortByOpenStatus(filterRestaurants(allRestaurants)), [filterRestaurants, allRestaurants]);
  const isFiltering = !!activeCategory || !!searchQuery.trim();
  return <div className="min-h-screen w-full flex flex-col bg-[#0B0B0B]" data-id="element-5259">
      <MarketplaceHeader data-id="element-5260" />

      <main className="flex-1" data-id="element-5261">
        <HeroSection searchQuery={searchQuery} onSearchChange={setSearchQuery} data-id="element-5262" />

        <div className="max-w-7xl mx-auto py-8" data-id="element-5263">
          {/* Categories */}
          {loading ? <div className="px-4 sm:px-6 lg:px-8 mb-12 flex gap-4 overflow-x-auto no-scrollbar" data-id="element-5264">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-12 w-32 rounded-full shrink-0 bg-[#1a1a1a] animate-pulse" data-id="element-5265" />)}
            </div> : <div className="mb-12" data-id="element-5266">
              <CategoryChips categories={categories} activeCategory={activeCategory} onSelect={setActiveCategory} data-id="element-5267" />
            </div>}

          {/* Featured Section — hide when filtering yields 0 featured */}
          {filteredFeatured.length > 0 && <section className="px-4 sm:px-6 lg:px-8 mb-16" data-id="element-5268">
              <div className="flex items-center justify-between mb-8" data-id="element-5269">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-5270">
                  <span className="w-1.5 h-7 bg-[#C9A34E] rounded-full" data-id="element-5271" />
                  {t('featured')} {t('restaurants')}
                </h2>
              </div>

              {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5272">
                  {[1, 2, 3].map(i => <div key={i} className="h-[300px] rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-5273" />)}
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5274">
                  {filteredFeatured.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} zoneName={zones.find(z => z.id === restaurant.zone_id)?.name} data-id="element-5275" />)}
                </div>}
            </section>}

          {/* All Restaurants Section */}
          <section className="px-4 sm:px-6 lg:px-8 mb-16" data-id="element-5276">
            <div className="flex items-center justify-between mb-8" data-id="element-5277">
              <div data-id="element-5278">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-5279">
                  <span className="w-1.5 h-7 bg-[#C9A34E] rounded-full" data-id="element-5280" />
                  {isFiltering ? 'Resultados' : t('all_restaurants')}
                </h2>
                {!isFiltering && <p className="text-sm text-white/40 mt-1 pl-5" data-id="element-5281">
                    Cada restaurante gestiona sus pedidos directamente
                  </p>}
                {isFiltering && <p className="text-sm text-white/40 mt-1 pl-5" data-id="element-5282">
                    {filteredAll.length} restaurante
                    {filteredAll.length !== 1 ? 's' : ''} encontrado
                    {filteredAll.length !== 1 ? 's' : ''}
                  </p>}
              </div>
            </div>

            {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5283">
                {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-[300px] rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-5284" />)}
              </div> : filteredAll.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5285">
                {filteredAll.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} zoneName={zones.find(z => z.id === restaurant.zone_id)?.name} data-id="element-5286" />)}
              </div> : <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-5287">
                <Store className="w-10 h-10 text-white/20 mx-auto mb-3" data-id="element-5288" />
                <p className="text-white/60 font-medium" data-id="element-5289">
                  {isFiltering ? 'No se encontraron restaurantes con esos filtros' : 'No se encontraron restaurantes'}
                </p>
                <p className="text-white/30 text-sm mt-1" data-id="element-5290">
                  {isFiltering ? 'Prueba con otra categoría o término de búsqueda.' : 'Aún no hay restaurantes disponibles. Vuelve pronto.'}
                </p>
                {isFiltering && <button onClick={() => {
              setActiveCategory('');
              setSearchQuery('');
            }} className="mt-4 px-6 py-2 rounded-full bg-[#C9A34E]/10 text-[#C9A34E] text-sm font-semibold border border-[#C9A34E]/30 hover:bg-[#C9A34E]/20 transition-colors" data-id="element-5291">
                    Limpiar filtros
                  </button>}
              </div>}
          </section>
        </div>
      </main>

      <Footer data-id="element-5292" />
    </div>;
}